import React, { useState } from 'react';

const QuerySection = () => {
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.name.trim()) {
      setSubmitStatus({ type: 'error', message: 'Name is required' });
      return;
    }
    
    if (!formData.email.trim()) {
      setSubmitStatus({ type: 'error', message: 'Email is required' });
      return;
    }
    
    if (!formData.subject.trim()) {
      setSubmitStatus({ type: 'error', message: 'Subject is required' });
      return;
    }
    
    if (!formData.message.trim()) {
      setSubmitStatus({ type: 'error', message: 'Message is required' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          subject: formData.subject.trim(),
          phone: formData.phone.trim() || null,
          message: formData.message.trim(),
          queryType: 'GENERAL',
          priority: 'MEDIUM'
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus({ 
          type: 'success', 
          message: result.message || 'Thank you for your inquiry. We\'ll get back to you soon!' 
        });
        setFormData({ name: '', subject: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus({ 
          type: 'error', 
          message: result.error || 'Failed to send message. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const styles = {
    section: {
      backgroundColor: '#ffffff',
      padding: '5rem 0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif'
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 2rem',
      display: 'grid',
      gridTemplateColumns: '1fr 400px',
      gap: '4rem',
      alignItems: 'start'
    },
    mainTitle: {
      fontSize: '2.5rem',
      fontWeight: '700',
      color: '#1f2937',
      margin: '0 0 1.5rem 0',
      lineHeight: '1.2',
      letterSpacing: '-0.02em'
    },
    mainText: {
      fontSize: '1rem',
      color: '#4b5563',
      lineHeight: '1.6',
      margin: '0 0 1rem 0'
    },
    highlight: {
      color: '#059669',
      fontWeight: '600'
    },
    rightSection: {
      display: 'flex',
      flexDirection: 'column'
    },
    formContainer: {
      backgroundColor: '#f8fafc',
      padding: '2rem',
      borderRadius: '12px',
      border: '1px solid #e2e8f0'
    },
    formTitle: {
      fontSize: '1.6rem',
      fontWeight: '700',
      color: '#1f2937',
      margin: '0 0 0.8rem 0'
    },
    formSubtitle: {
      fontSize: '0.9rem',
      color: '#6b7280',
      lineHeight: '1.5',
      margin: '0 0 1.5rem 0'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.2rem'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.4rem'
    },
    label: {
      fontSize: '0.85rem',
      fontWeight: '600',
      color: '#374151'
    },
    input: {
      padding: '0.8rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.9rem',
      backgroundColor: 'white',
      transition: 'border-color 0.2s ease',
      outline: 'none'
    },
    textarea: {
      padding: '0.8rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.9rem',
      backgroundColor: 'white',
      minHeight: '80px',
      resize: 'vertical',
      fontFamily: 'inherit',
      transition: 'border-color 0.2s ease',
      outline: 'none'
    },
    submitButton: {
      background: isSubmitting 
        ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' 
        : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      color: 'white',
      border: 'none',
      padding: '0.8rem 1.5rem',
      borderRadius: '6px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: isSubmitting ? 'not-allowed' : 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      opacity: isSubmitting ? 0.7 : 1
    },
    statusMessage: {
      padding: '0.8rem',
      borderRadius: '6px',
      fontSize: '0.85rem',
      fontWeight: '500',
      marginBottom: '1rem'
    },
    successMessage: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
      border: '1px solid #a7f3d0'
    },
    errorMessage: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      border: '1px solid #fca5a5'
    }
  };

  return (
    <div>
      <style>{`
        .form-input:focus, .form-textarea:focus {
          border-color: #059669;
          box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
        }
        
        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(5, 150, 105, 0.3);
        }
        
        .submit-btn:disabled {
          transform: none;
          box-shadow: none;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 1024px) {
          .main-container {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
          
          .main-title {
            font-size: 2.2rem !important;
          }
        }
        
        @media (max-width: 768px) {
          .main-container {
            padding: 0 1.5rem !important;
          }
          
          .main-title {
            font-size: 2rem !important;
          }
          
          .form-container {
            padding: 1.5rem !important;
          }
        }
        
        @media (max-width: 640px) {
          .main-container {
            padding: 0 1rem !important;
          }
          
          .main-title {
            font-size: 1.8rem !important;
          }
          
          .form-title {
            font-size: 1.4rem !important;
          }
        }
      `}</style>

      <section style={styles.section}>
        <div style={styles.container} className="main-container">
          {/* Main Content */}
          <div className="main-content">
            <h2 style={styles.mainTitle} className="main-title">
              We are bridging the gap between the market and your goals.
            </h2>
            
            <p style={styles.mainText}>
              It's very simple one size does not fit.
            </p>
            <p style={styles.mainText}>
              At <span style={styles.highlight}>WinNest</span>, we evaluate the market in relation to your 
              financial needs and, as a result, we tailor an investing plan for you.
            </p>
            
            <p style={styles.mainText}>
              An ideal investment objective should be measurable and reachable. WinNest develops clear, 
              acceptable, and practical goals that are simple to understand and implement.
            </p>
            
            <p style={styles.mainText}>
              A broad investment strategy with numerous possibilities should be designed to fulfil your 
              financial goals step by step.
            </p>
          </div>

          {/* Contact Form */}
          <div style={styles.rightSection} className="contact-form">
            <div style={styles.formContainer} className="form-container">
              <h3 style={styles.formTitle} className="form-title">
                Ask us anything
              </h3>
              <p style={styles.formSubtitle}>
                WinNest helps you reach your financial objectives, whether they be everyday goals 
                like paying your bills or long-term goals like a financially secure retirement. 
                In case of queries, you write us here.
              </p>
              
              <div style={styles.form}>
                {/* Status Message */}
                {submitStatus.message && (
                  <div 
                    style={{
                      ...styles.statusMessage,
                      ...(submitStatus.type === 'success' ? styles.successMessage : styles.errorMessage)
                    }}
                  >
                    {submitStatus.message}
                  </div>
                )}
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Name *</label>
                  <input
                    type="text"
                    style={styles.input}
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter your full name"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Subject *</label>
                  <input
                    type="text"
                    style={styles.input}
                    className="form-input"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="Enter subject"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>E-mail *</label>
                  <input
                    type="email"
                    style={styles.input}
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter your email"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Phone Number</label>
                  <input
                    type="tel"
                    style={styles.input}
                    className="form-input"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Enter your phone number"
                    disabled={isSubmitting}
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Write here *</label>
                  <textarea
                    style={styles.textarea}
                    className="form-textarea"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Write your message here..."
                    required
                    disabled={isSubmitting}
                  />
                </div>
                
                <button
                  type="button"
                  style={styles.submitButton}
                  className="submit-btn"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                      <span>Sending...</span>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                    </div>
                  ) : (
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                      <span>Send message</span>
                      <span style={{fontSize: '1.2rem'}}>→</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuerySection;